import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, Users, Globe, BookOpen, Palette, RefreshCw, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ContextData {
  id: string;
  data: any;
  lastUpdated: string;
}

interface ContextPanelProps {
  projectId: number;
  onContextUpdate?: () => void;
}

export function ContextPanel({ projectId, onContextUpdate }: ContextPanelProps) {
  const [contexts, setContexts] = useState<Record<string, ContextData[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadContexts();
  }, [projectId]);

  const loadContexts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/ai/context/${projectId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setContexts(data.contexts || {});
      } else {
        toast({
          title: "Error",
          description: "Failed to load story context",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading contexts:', error);
      toast({
        title: "Error",
        description: "Failed to load story context",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeContext = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch(`/api/ai/analyze-context/${projectId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Context Analyzed",
          description: `${data.contextsCreated} context elements analyzed successfully`
        });
        loadContexts();
        onContextUpdate?.();
      } else {
        const error = await response.json();
        toast({
          title: "Analysis Failed",
          description: error.message || "Failed to analyze story context",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error analyzing context:', error);
      toast({
        title: "Error",
        description: "Failed to analyze story context",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getContextIcon = (type: string) => {
    switch (type) {
      case 'character': return <Users className="h-4 w-4" />;
      case 'world': return <Globe className="h-4 w-4" />;
      case 'plot': return <BookOpen className="h-4 w-4" />;
      case 'style': return <Palette className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  const formatContextType = (type: string) => {
    switch (type) {
      case 'character': return 'Characters';
      case 'world': return 'World Building';
      case 'plot': return 'Plot Structure';
      case 'style': return 'Writing Style';
      default: return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const renderContextData = (type: string, data: any) => {
    switch (type) {
      case 'character':
        return (
          <div className="space-y-2">
            <div className="font-medium">{data.name}</div>
            {data.personality && (
              <div className="text-sm text-muted-foreground">
                <strong>Personality:</strong> {data.personality}
              </div>
            )}
            {data.motivations && (
              <div className="text-sm text-muted-foreground">
                <strong>Motivations:</strong> {data.motivations}
              </div>
            )}
            {data.arc && (
              <div className="text-sm text-muted-foreground">
                <strong>Character Arc:</strong> {data.arc}
              </div>
            )}
          </div>
        );
      
      case 'world':
        return (
          <div className="space-y-2">
            {data.setting && (
              <div className="text-sm">
                <strong>Setting:</strong> {data.setting}
              </div>
            )}
            {data.timeperiod && (
              <div className="text-sm">
                <strong>Time Period:</strong> {data.timeperiod}
              </div>
            )}
            {data.locations && Array.isArray(data.locations) && (
              <div className="text-sm">
                <strong>Locations:</strong> {data.locations.join(', ')}
              </div>
            )}
          </div>
        );
      
      case 'plot':
        return (
          <div className="space-y-2">
            {data.mainConflict && (
              <div className="text-sm">
                <strong>Main Conflict:</strong> {data.mainConflict}
              </div>
            )}
            {data.themes && Array.isArray(data.themes) && (
              <div className="text-sm">
                <strong>Themes:</strong> {data.themes.join(', ')}
              </div>
            )}
            {data.structure && (
              <div className="text-sm">
                <strong>Structure:</strong> {data.structure}
              </div>
            )}
          </div>
        );
      
      case 'style':
        return (
          <div className="space-y-2">
            {data.tone && (
              <div className="text-sm">
                <strong>Tone:</strong> {data.tone}
              </div>
            )}
            {data.voiceStyle && (
              <div className="text-sm">
                <strong>Voice:</strong> {data.voiceStyle}
              </div>
            )}
            {data.vocabularyLevel && (
              <div className="text-sm">
                <strong>Vocabulary:</strong> {data.vocabularyLevel}
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        );
    }
  };

  const hasContexts = Object.keys(contexts).length > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Brain className="h-4 w-4" />
          Story Context
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={analyzeContext}
          disabled={isAnalyzing}
          className="h-8"
        >
          {isAnalyzing ? (
            <RefreshCw className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Analyze
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">
            Loading context...
          </div>
        ) : !hasContexts ? (
          <div className="text-center py-4 space-y-2">
            <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              No story context available yet
            </div>
            <div className="text-xs text-muted-foreground">
              Click "Analyze" to extract context from your story
            </div>
          </div>
        ) : (
          <Tabs defaultValue={Object.keys(contexts)[0]} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              {contexts && typeof contexts === 'object' && Object.keys(contexts).map(type => (
                <TabsTrigger 
                  key={type} 
                  value={type}
                  className="text-xs flex items-center gap-1"
                >
                  {getContextIcon(type)}
                  {formatContextType(type)}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {contexts && typeof contexts === 'object' && Object.entries(contexts).map(([type, contextList]) => (
              <TabsContent key={type} value={type} className="mt-4">
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {Array.isArray(contextList) && contextList.map((context, index) => (
                      <div key={context.id} className="border rounded p-3 space-y-2">
                        {renderContextData(type, context.data)}
                        <div className="flex justify-between items-center">
                          <Badge variant="secondary" className="text-xs">
                            {formatContextType(type)}
                          </Badge>
                          <div className="text-xs text-muted-foreground">
                            Updated {new Date(context.lastUpdated).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}